import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';

import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { CONTACTS_PAGE_QUERY } from '@/data/contactsPageQuery';
import { Section } from '@/components/common/Section';
import { Heading } from '@/components/typography';
import { SocialList } from '@/components/cards/SocialList';
import Image from 'next/image';
import { PhoneList } from '@/components/cards/PhoneList';
import { EmailList } from '@/components/cards/EmailList';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import Map from '@/components/common/Map';
import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { PageHead } from '@/components/common/PageHead';
import { FeedbackForm } from '@/components/forms/FeedbackForm';

const Page = ({ data, language }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const { seo, title, featuredImage } = data?.pageBy?.translation ?? {};
  const contacts =
    language === 'UK'
      ? data?.acfOptionsKontakty?.contactsPageSettings.contacts
      : data?.acfOptionsKontakty?.contactsPageEn.contactsEn;
  const featureImage = featuredImage?.node;
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Section
            className="py-10"
            breadcrumbs={seo?.breadcrumbs}
            lazy={false}
          >
            {title && (
              <Heading tag="h1" content={title} withDivider className="" />
            )}
            <div className="pb-[60px] pt-[30px] md:ml-auto md:max-w-[84%] md:pb-[100px] md:pt-[60px] lg:mx-auto lg:grid lg:grid-cols-[270px,_1fr] lg:gap-x-36 lg:pb-[110px]  lg:pt-0">
              {featureImage && (
                <div className="hidden lg:block lg:py-10">
                  <Image
                    src={featureImage?.mediaItemUrl ?? ''}
                    alt={featureImage?.altText ?? ''}
                    width={featureImage?.mediaDetails?.width}
                    height={featureImage?.mediaDetails?.height}
                  />
                </div>
              )}
              <ul>
                {Object.entries(contacts)
                  .filter(([key]) => key !== '__typename')
                  .map(([key, contact]) => (
                    <li
                      key={key}
                      className="grid gap-5 border-b border-primaryLight py-8 md:grid-cols-2 md:gap-10 md:py-10"
                    >
                      <Heading
                        tag="h3"
                        content={contact.title}
                        color="accent-main"
                        className="text-base font-normal dark:text-darkGrayPM md:text-lg"
                      />
                      <div className="pl-[25%] md:pl-0">
                        {contact.description && (
                          <p
                            dangerouslySetInnerHTML={{
                              __html: contact.description,
                            }}
                          />
                        )}
                        {contact.phone && contact.phone.length > 0 && (
                          <PhoneList data={contact.phone} />
                        )}
                        {contact.email && contact.email.length > 0 && (
                          <EmailList data={contact.email} />
                        )}
                        {key === 'socialMedia' &&
                          contact.item &&
                          contact.item.length > 0 && (
                            <SocialList data={contact.item} iconColor="black" />
                          )}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
            <FeedbackForm />
            <AnimatedCard className="h-[360px] md:h-[310px] lg:h-[394px]">
              <Map />
            </AnimatedCard>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();

  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;

  const language = locale?.toUpperCase();

  const { data } = await client.query({
    query: CONTACTS_PAGE_QUERY,
    variables: {
      language,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      language,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
